import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { canUserManageStaff } from "@/lib/staffPermissions";

export default function NuovaValutazione() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [staff, setStaff] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [risposte, setRisposte] = useState([]);
  const [openCommento, setOpenCommento] = useState({});
  const [commentoGen, setCommentoGen] = useState("");
  const [periodo, setPeriodo] = useState(
    `${new Date().getFullYear()}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, q] = await Promise.all([
          api.get(`/staff/${id}`),
          api.get("/evaluation-questions"),
        ]);
        if (!alive) return;
        setStaff(s.data);
        const attive = (q.data || []).filter((qq) => qq.attiva);
        setQuestions(attive);
        setRisposte(
          attive.map((qq) => ({
            domanda_id: qq.id,
            domanda_label: qq.label,
            punteggio: 7,
            commento: "",
            touched: false,
          }))
        );
        // RBAC pre-check allineato al backend (auth.can_user_manage_staff)
        if (!canUserManageStaff(user, s.data)) {
          toast.error("Non hai i permessi per valutare questo dipendente");
          navigate(`/staff/${id}`, { replace: true });
          return;
        }
        // Fetch hotel for subtitle
        try {
          const h = await api.get(`/hotels/${s.data.hotel_id}`);
          if (alive) setHotel(h.data);
        } catch { /* ignore */ }
      } catch (err) {
        toast.error(formatApiError(err));
        navigate(`/staff/${id}`, { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const media = useMemo(() => {
    if (!risposte.length) return "0.0";
    return (risposte.reduce((a, r) => a + r.punteggio, 0) / risposte.length).toFixed(1);
  }, [risposte]);

  const touchedCount = risposte.filter((r) => r.touched).length;
  const progressPct = risposte.length ? Math.round((touchedCount / risposte.length) * 100) : 0;

  const updatePunteggio = (idx, val) => {
    setRisposte((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], punteggio: val, touched: true };
      return copy;
    });
  };

  const updateCommento = (idx, val) => {
    setRisposte((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], commento: val };
      return copy;
    });
  };

  const toggleCommento = (idx) => {
    setOpenCommento((s) => ({ ...s, [idx]: !s[idx] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = risposte.map(({ touched: _t, ...rest }) => rest);
      await api.post(`/staff/${id}/reviews`, {
        periodo,
        risposte: payload,
        commento_generale: commentoGen,
      });
      toast.success(`Valutazione salvata · media ${media}/10`);
      navigate(`/staff/${id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Caricamento questionario…
      </div>
    );
  }
  if (!staff) return null;

  return (
    <div className="ih-anim-in -mx-4 sm:-mx-6 lg:-mx-8 -my-4 sm:-my-6 lg:-my-8" data-testid="nuova-valutazione-page">
      <form onSubmit={submit} className="flex flex-col min-h-[calc(100vh-3.5rem)]">
        {/* Header sticky */}
        <header
          className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border/60"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-4 pb-3">
            <div className="flex items-center gap-3 mb-2">
              <button
                type="button"
                onClick={() => navigate(`/staff/${id}`)}
                className="h-9 w-9 -ml-2 rounded-lg hover:bg-accent flex items-center justify-center shrink-0"
                data-testid="back-btn"
                aria-label="Indietro"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nuova valutazione</div>
                <h1 className="font-display text-lg sm:text-xl tracking-tight truncate">
                  {staff.nome} {staff.cognome}
                </h1>
                <div className="text-xs text-muted-foreground truncate">
                  {staff.ruolo}{hotel ? ` · ${hotel.nome}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
              <span data-testid="review-progress">{touchedCount}/{risposte.length} risposte · {progressPct}%</span>
              <input
                data-testid="review-periodo"
                type="text"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                placeholder="2026-Q2"
                className="h-7 px-2 text-[11px] rounded-md bg-accent/50 border border-border/40 w-24 text-right"
                aria-label="Periodo"
              />
            </div>
            <div className="h-1 bg-accent/50 rounded-full overflow-hidden">
              <div className="h-full bg-lavender-500 transition-all duration-300" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 py-4 pb-32 sm:pb-28" data-testid="review-body">
          <ul className="divide-y divide-border/60">
            {risposte.map((r, idx) => {
              const isOpen = !!openCommento[idx];
              return (
                <li key={r.domanda_id} className="py-4" data-testid={`q-${idx}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <span className="text-[15px] leading-snug font-medium flex-1 min-w-0">
                      <span className="text-muted-foreground mr-1.5">{idx + 1}.</span>{r.domanda_label}
                    </span>
                    <span className="text-lg font-semibold text-lavender-600 dark:text-lavender-400 tabular-nums whitespace-nowrap" data-testid={`q-${idx}-value`}>
                      {r.punteggio}<span className="text-[11px] text-muted-foreground font-normal ml-0.5">/10</span>
                    </span>
                  </div>
                  <input
                    data-testid={`q-${idx}-slider`}
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={r.punteggio}
                    onChange={(e) => updatePunteggio(idx, parseInt(e.target.value, 10))}
                    className="w-full accent-lavender-500 h-2 cursor-pointer"
                    style={{ minHeight: "32px" }}
                    aria-label={`Punteggio ${r.domanda_label}: ${r.punteggio} su 10`}
                  />
                  <button
                    type="button"
                    onClick={() => toggleCommento(idx)}
                    className="mt-1 text-[12px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
                    data-testid={`q-${idx}-toggle-comment`}
                  >
                    {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    {isOpen ? "Nascondi commento" : (r.commento ? `Commento: "${r.commento.slice(0, 30)}${r.commento.length > 30 ? "…" : ""}"` : "Aggiungi commento")}
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-200"
                    style={{ maxHeight: isOpen ? "200px" : "0px", marginTop: isOpen ? "8px" : "0px" }}
                  >
                    <textarea
                      data-testid={`q-${idx}-commento`}
                      value={r.commento}
                      onChange={(e) => updateCommento(idx, e.target.value)}
                      placeholder="Commento (opzionale)…"
                      rows={2}
                      className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm resize-none"
                    />
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Commento generale */}
          <div className="mt-6 pt-4 border-t border-border/60">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Commento generale (opzionale)</label>
            <textarea
              data-testid="review-commento-generale"
              value={commentoGen}
              onChange={(e) => setCommentoGen(e.target.value)}
              placeholder="Note di sintesi facoltative sul periodo…"
              rows={3}
              className="w-full mt-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer sticky */}
        <footer
          className="sticky bottom-0 z-20 bg-background/95 backdrop-blur border-t border-border/60"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Media</div>
              <div className="font-display text-2xl leading-none">
                <span data-testid="review-media">{media}</span>
                <span className="text-xs text-muted-foreground ml-0.5">/10</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="review-submit"
              className="px-5 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-transform hover:scale-[1.02] active:scale-[0.98]"
              style={{ minHeight: "48px" }}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {saving ? "Salvataggio…" : "Salva valutazione"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
